import { CellContext, createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import Button from 'base-components/Button'
import classNames from 'classnames'
import { useScene } from 'providers/SceneProvider'
import { useState } from 'react'
import { getSlides } from 'utils/getData'

type VisualStageInterface = {
  id: string
  index: number
  title: string
  audienceCapacity: string
  website: string
  link: number
}

const columnHelper = createColumnHelper<VisualStageInterface>()

const SceneTable = () => {
  const { setSelectedSlideFromId, toggleShowTable, selectedSlide } = useScene()
  const handleClick = (data: CellContext<VisualStageInterface, number>) => () => {
    setSelectedSlideFromId(data?.row?.original?.id ?? '')
    toggleShowTable()
  }

  const columns = [
    columnHelper.accessor('index', {
      header: () => <span>#</span>,
      footer: () => <span>#</span>,
      cell: (info) => {
        return info.getValue()
      },
    }),
    columnHelper.accessor('title', {
      header: () => <span>Namn</span>,
      footer: () => <span>Namn</span>,
      cell: (info) => <b>{info.getValue()}</b>,
    }),
    columnHelper.accessor((row) => row.audienceCapacity, {
      id: 'audienceCapacity',
      cell: (info) => <i>{info.getValue()}</i>,
      header: () => <span>Publikstorlek</span>,
      footer: () => <span>Publikstorlek</span>,
    }),
    columnHelper.accessor('website', {
      header: () => 'Hemsida',
      footer: () => 'Hemsida',
      cell: (info) => <a href={info.renderValue() ?? ''}>Hemsida</a>,
    }),
    columnHelper.accessor('link', {
      header: () => 'Länk till karta',
      footer: () => 'Länk till karta',
      cell: (info) => {
        return (
          <div className='relative flex justify-center items-center pt-1 h-10'>
            <Button
              className='flex-1 shadow-none  py-1'
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              onClick={handleClick(info)}
            >
              Visa
            </Button>
          </div>
        )
      },
    }),
  ]

  const defaultData: VisualStageInterface[] = getSlides().map((slide, index: number) => ({
    id: slide.id,
    index: index + 1,
    title: slide.title,
    audienceCapacity: slide?.properties?.audienceCapacity ?? '',
    website: slide?.properties?.website ?? '',
    link: index,
  }))

  const [data] = useState(() => [...defaultData])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className='bg-white rounded-lg shadow overflow-auto'>
      <table className='min-w-[600px] text-sm'>
        <thead className=''>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className=''>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className='text-left px-4 py-2'>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className='px-4'>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className='hover:bg-gray-50'>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={classNames('px-4 py-1', selectedSlide.id === row.original.id ? 'bg-blue-50' : '')}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default SceneTable
